'use client'

import { MapPin } from 'lucide-react'
import Image from 'next/image'
import React from 'react'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { cn } from '@/utilities/ui'

import {
  buildStripSlidePresets,
  stripCarouselControlClassName,
  stripCarouselSlideClassName,
  stripCarouselTrackClassName,
  stripShelfTopClass,
  type ImageGalleryStripSlide,
} from './imageGalleryStrip'

type ImageGalleryStripCarouselProps = {
  ariaLabel: string
  bgColor?: string
  carouselNextLabel: string
  carouselPreviousLabel: string
  className?: string
  loop?: boolean
  pullUp?: boolean
  slides: ImageGalleryStripSlide[]
}

export const ImageGalleryStripCarousel: React.FC<ImageGalleryStripCarouselProps> = ({
  ariaLabel,
  bgColor = 'bg-bluetext',
  carouselNextLabel,
  carouselPreviousLabel,
  className,
  loop = true,
  pullUp = true,
  slides,
}) => {
  const stripSlides = [...buildStripSlidePresets(slides), ...buildStripSlidePresets(slides)]

  return (
    <div
      className={cn(
        'relative z-20 w-full',
        pullUp ? '-mt-14 sm:-mt-16 lg:-mt-24' : 'mt-0',
        className,
      )}
    >
      {pullUp ? (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-x-0 -bottom-px',
            bgColor,
            stripShelfTopClass,
          )}
        />
      ) : null}

      <Carousel
        aria-label={ariaLabel}
        className="relative w-full"
        opts={{ align: 'start', loop }}
      >
        <CarouselPrevious
          aria-label={carouselPreviousLabel}
          className={cn(stripCarouselControlClassName, 'left-2 lg:left-8')}
          size="icon"
          variant="outline"
        />
        <CarouselNext
          aria-label={carouselNextLabel}
          className={cn(stripCarouselControlClassName, 'right-2 lg:right-8')}
          size="icon"
          variant="outline"
        />

        <CarouselContent
          className={stripCarouselTrackClassName}
          role="list"
          viewportClassName="overflow-x-clip overflow-y-visible"
        >
          {stripSlides.map((slide, index) => (
            <CarouselItem
              key={`${slide.id}-${index}`}
              className={cn(
                'relative',
                slide.slideClassName,
                stripCarouselSlideClassName,
                slide.zClassName,
              )}
              role="listitem"
            >
              <div
                className={cn(
                  'relative aspect-video w-full overflow-hidden rounded-[10px]',
                  slide.frameClassName,
                )}
              >
                <Image
                  alt={slide.imageAlt}
                  className="object-cover"
                  fill
                  sizes="(max-width: 1024px) 80vw, 28rem"
                  src={slide.image}
                />
                {slide.locationLabel ? (
                  <div className="absolute bottom-3 left-1/2 inline-flex max-w-[calc(100%-1.5rem)] -translate-x-1/2 items-center gap-0.5 rounded-full border border-black/10 bg-black/70 px-2.5 py-1.5 font-outfit text-base font-medium text-white shadow-[0px_6px_20px_0px_rgba(9,114,82,0.12)] backdrop-blur-[2px]">
                    <MapPin aria-hidden className="size-4 shrink-0 text-turquoise" />
                    <span className="truncate">{slide.locationLabel}</span>
                  </div>
                ) : null}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  )
}
